import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServicesPage = () => {
  const staticServices = [
    {
      id: "exterior-interior",
      name: "Külső–Belső Detailing",
      description: "Teljes körű autótisztítás kívül és belül, amely magában foglalja a kézi mosást, belső porszívózást, műszerfal tisztítást és gumiápolást.",
      price_from: 45000,
      duration: "3-4 óra",
      image: "https://images.unsplash.com/photo-1607860108855-64acf2078ed9?q=80&w=800&auto=format&fit=crop"
    },
    {
      id: "ceramic-coating",
      name: "Kerámia Bevonat",
      description: "Hosszú távú védelem a lakk számára 9H keménységű kerámia bevonattal, amely 2-5 évig védi autóját a környezeti hatásoktól.",
      price_from: 150000,
      duration: "6-8 óra",
      image: "https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?q=80&w=800&auto=format&fit=crop"
    },
    {
      id: "polishing",
      name: "Gépi Polírozás",
      description: "Fényvisszaállítás és karcoláseltávolítás gépi polírozással. Visszaadja autója eredeti ragyogását és megszünteti az örvénykarcolásokat.",
      price_from: 80000,
      duration: "4-6 óra",
      image: "https://images.unsplash.com/photo-1616422285623-13ff0162193c?q=80&w=800&auto=format&fit=crop"
    },
    {
      id: "leather-care",
      name: "Bőrápolás",
      description: "Professzionális bőrtisztítás és kondicionálás, amely megújítja és védi autója bőrüléseit. Megelőzi a repedezést és visszaadja az eredeti színt.",
      price_from: 35000,
      duration: "2-3 óra",
      image: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=800&auto=format&fit=crop"
    },
    {
      id: "ozone",
      name: "Ózonos Fertőtlenítés",
      description: "Teljes körű belső fertőtlenítés és szagmentesítés ózonnal. Elpusztítja a baktériumokat, vírusokat és megszünteti a kellemetlen szagokat.",
      price_from: 25000,
      duration: "1-2 óra",
      image: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?q=80&w=800&auto=format&fit=crop"
    },
    {
      id: "premium-wash",
      name: "Prémium Kézi Mosás",
      description: "Professzionális kézi autómosás két vödör módszerrel, amely biztosítja, hogy autója károsodásmentesen tiszta legyen.",
      price_from: 15000,
      duration: "1 óra",
      image: "https://images.unsplash.com/photo-1601362840469-51e4d8d58785?q=80&w=800&auto=format&fit=crop"
    }
  ];

  const [services, setServices] = useState(staticServices);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data.services);
      } catch (error) {
        console.error("Error fetching services:", error);
        setServices(staticServices);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const serviceFeatures = {
    "exterior-interior": [
      "Kézi mosás pH-semleges samponnal",
      "Teljes belső porszívózás",
      "Műszerfal és konzol tisztítás",
      "Ablaktisztítás kívül-belül",
      "Gumiápolás és fényezés",
      "Felnitisztítás"
    ],
    "ceramic-coating": [
      "Felület előkészítés és dekontamináció",
      "Gépi polírozás",
      "Kerámia bevonat alkalmazása",
      "9H keménységű védelem",
      "UV és vegyszer ellenállás",
      "2-5 év tartósság"
    ],
    "polishing": [
      "Lakk állapotfelmérés",
      "Dekontamináció",
      "Örvénykarcolás eltávolítás",
      "Oxidáció megszüntetése",
      "Fényvisszaállítás",
      "Védőréteg alkalmazás"
    ],
    "leather-care": [
      "Bőr állapotfelmérés",
      "Mélytisztítás speciális szerrel",
      "Kondicionálás és táplálás",
      "UV védelem",
      "Repedezés megelőzés",
      "Eredeti szín visszaállítás"
    ],
    "ozone": [
      "Fertőtlenítés ózonnal",
      "Baktérium elpusztítás",
      "Szagmentesítés",
      "Allergia megelőzés",
      "Penész eltávolítás",
      "Friss illat"
    ],
    "premium-wash": [
      "Előmosás és habosítás",
      "Kézi mosás két vödör módszerrel",
      "Felnitisztítás",
      "Ablaktisztítás",
      "Gumiápolás",
      "Végső szárítás"
    ]
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="services-page" className="bg-[#050505]">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-6">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4 block">
              Szolgáltatásaink
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl text-[#E5E5E5] mb-6">
              PRÉMIUM DETAILING
              <span className="block text-[#D4AF37]">SZOLGÁLTATÁSOK</span>
            </h1>
            <p className="text-[#A3A3A3] text-lg leading-relaxed">
              Professzionális autókozmetikai szolgáltatásaink a legmagasabb minőségi 
              követelményeknek megfelelően készülnek. Minden részletre odafigyelünk.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6">
          <div className="space-y-24">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
                data-testid={`service-detail-${service.id}`}
              >
                {/* Image */}
                <div className={`${index % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="relative group overflow-hidden border border-[#2A2A2A]">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-60" />
                    <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-[#050505]/80 px-4 py-2">
                      <Clock size={16} className="text-[#D4AF37]" />
                      <span className="text-[#E5E5E5] text-sm">{service.duration}</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className={`${index % 2 === 1 ? "lg:order-1" : ""}`}>
                  <h2 className="font-heading text-3xl text-[#E5E5E5] mb-4">
                    {service.name}
                  </h2>
                  <p className="text-[#A3A3A3] mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                    {(serviceFeatures[service.id] || []).map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-[#D4AF37] flex-shrink-0" />
                        <span className="text-[#A3A3A3] text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Price & CTA */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                    <div>
                      <span className="text-[#666] text-sm block">Ártól</span>
                      <span className="font-heading text-3xl text-[#D4AF37]">
                        {service.price_from.toLocaleString()} Ft
                      </span>
                    </div>
                    <Link to="/idopontfoglalas">
                      <Button
                        data-testid={`book-${service.id}`}
                        className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest hover:bg-[#B5952F] px-6 py-3 rounded-sm"
                      >
                        Foglalás
                        <ArrowRight className="ml-2" size={18} />
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#121212]">
        <div className="container mx-auto px-6 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#E5E5E5] mb-6">
              NEM TALÁLJA A MEGFELELŐT?
            </h2>
            <p className="text-[#A3A3A3] mb-8 max-w-xl mx-auto">
              Vegye fel velünk a kapcsolatot egyedi igényeivel, és személyre szabott 
              ajánlatot készítünk Önnek.
            </p>
            <Link to="/kapcsolat">
              <Button
                data-testid="contact-btn"
                variant="outline"
                className="bg-transparent border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-8 py-4 uppercase tracking-widest rounded-sm"
              >
                Kapcsolat
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
